import React from 'react';
import { Spinner } from 'react-bootstrap';

export type LomavisStatusCard1Variant =
  | 'info'
  | 'success'
  | 'warning'
  | 'danger'
  | 'neutral';

export interface LomavisStatusCard1Props {
  variant?: LomavisStatusCard1Variant;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Font Awesome icon class (without the leading `fa-solid`). Falls back to the variant default. */
  icon?: string;
  /** Show a spinner instead of the icon (e.g. while an async status is in flight). */
  busy?: boolean;
  /** Optional trailing content — a link, button, badge. Renders on the right on md+, below on narrow. */
  action?: React.ReactNode;
  className?: string;
  /** Accessibility role. Defaults to "status" for info/success/neutral, "alert" for danger/warning. */
  role?: 'status' | 'alert';
}

interface Theme {
  accent: string;
  softBg: string;
  ring: string;
  defaultIcon: string;
}

const themes: Record<LomavisStatusCard1Variant, Theme> = {
  info: {
    accent: '#0d6efd',
    softBg: 'rgba(13, 110, 253, 0.08)',
    ring: 'rgba(13, 110, 253, 0.25)',
    defaultIcon: 'fa-circle-info'
  },
  success: {
    accent: '#198754',
    softBg: 'rgba(25, 135, 84, 0.08)',
    ring: 'rgba(25, 135, 84, 0.25)',
    defaultIcon: 'fa-circle-check'
  },
  warning: {
    accent: '#fd7e14',
    softBg: 'rgba(253, 126, 20, 0.08)',
    ring: 'rgba(253, 126, 20, 0.25)',
    defaultIcon: 'fa-triangle-exclamation'
  },
  danger: {
    accent: '#dc3545',
    softBg: 'rgba(220, 53, 69, 0.08)',
    ring: 'rgba(220, 53, 69, 0.25)',
    defaultIcon: 'fa-triangle-exclamation'
  },
  neutral: {
    accent: '#6c757d',
    softBg: 'rgba(108, 117, 125, 0.08)',
    ring: 'rgba(108, 117, 125, 0.25)',
    defaultIcon: 'fa-circle-info'
  }
};

const defaultRole = (
  variant: LomavisStatusCard1Variant
): 'status' | 'alert' => (variant === 'danger' || variant === 'warning' ? 'alert' : 'status');

/**
 * Atoms: LomavisStatusCard1
 *
 * A soft-tinted card that surfaces a single piece of status information —
 * publishing state, sync outcome, a system message — with an icon on the
 * left, title + description in the middle, and an optional action on the
 * right. Matches the campaign builder's card styling (rounded-4, soft
 * shadow, subtle 25%-opacity accent border).
 */
export const LomavisStatusCard1: React.FC<LomavisStatusCard1Props> = ({
  variant = 'info',
  title,
  description,
  icon,
  busy = false,
  action,
  className = '',
  role
}) => {
  const theme = themes[variant];
  const resolvedIcon = icon || theme.defaultIcon;
  const resolvedRole = role || defaultRole(variant);

  return (
    <div
      className={`rounded-4 border bg-white shadow-sm p-3 p-md-4 d-flex flex-column flex-md-row align-items-md-center gap-3 ${className}`.trim()}
      style={{ borderColor: theme.ring }}
      role={resolvedRole}
    >
      <span
        aria-hidden
        className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
        style={{
          background: theme.softBg,
          color: theme.accent,
          height: 44,
          width: 44,
          fontSize: 20
        }}
      >
        {busy ? (
          <Spinner
            animation="border"
            size="sm"
            role="status"
            style={{ color: theme.accent }}
          />
        ) : (
          <i className={`fa-solid ${resolvedIcon}`} />
        )}
      </span>
      <div className="flex-grow-1 min-w-0">
        <div
          className="fw-semibold"
          style={{ color: theme.accent, fontSize: '1rem' }}
        >
          {title}
        </div>
        {description ? (
          <div className="text-body small mt-1">{description}</div>
        ) : null}
      </div>
      {action ? <div className="text-nowrap">{action}</div> : null}
    </div>
  );
};
